import {
    AppShell,
    Box,
    Burger,
    Button,
    CloseButton,
    Divider,
    Drawer,
    Group,
    Loader,
    NavLink,
    ScrollArea,
    Stack,
    Text,
    useMantineTheme,
} from '@mantine/core'
import {useDisclosure, useMediaQuery} from '@mantine/hooks'
import {
    IconArrowsSplit,
    IconBuilding,
    IconChartBar,
    IconCoin,
    IconHeartHandshake,
    IconHome,
    IconLayoutDashboard,
    IconLayoutSidebarLeftCollapse,
    IconLayoutSidebarLeftExpand,
    IconLogout,
    IconSettings,
    IconTargetArrow,
    IconTrendingUp,
    IconUser,
    IconUsers,
} from '@tabler/icons-react'
import {useQuery} from '@tanstack/react-query'
import {
    createFileRoute,
    Link,
    Outlet,
    redirect,
    useLocation,
    useMatch,
    useNavigate,
} from '@tanstack/react-router'
import {useEffect} from 'react'

import logoDarkUrl from 'src/assets/logo-dark.svg'
import SidebarNavLink from 'src/components/nav/nav-link'
import {ScenarioActiveBanner} from 'src/components/scenario/scenario-active-banner'
import {ScenarioProvider} from 'src/components/scenario/scenario-context'
import {ScenarioPlanningTrigger} from 'src/components/scenario/scenario-planning-trigger'

import {apiFetch} from '../api/client'
import {getOrganization} from '../api/client/api'
import type {OrganizationDetail} from '../api/client/models/organization'
import {clearStoredUser, getStoredUser} from '../lib/auth'

export const Route = createFileRoute('/_authed')({
    beforeLoad: () => {
        if (!getStoredUser()) {
            throw redirect({to: '/login'})
        }
    },
    component: AuthedLayout,
})

const NAVBAR_WIDTH_EXPANDED = 280
const NAVBAR_WIDTH_COLLAPSED = 64

type SidebarNavContentProps = {
    narrow: boolean
    adminMatch: boolean
    orgLoading: boolean
    org: OrganizationDetail | undefined
    orgId: string | undefined
}

function SidebarNavContent({narrow, adminMatch, orgLoading, org, orgId}: SidebarNavContentProps) {
    if (adminMatch) {
        return (
            <Stack gap={narrow ? 6 : 'xs'}>
                {!narrow && (
                    <Text fw={700} size="lg" mt="sm" mb="md" c="brand.9">
                        Admin
                    </Text>
                )}
                <SidebarNavLink
                    narrow={narrow}
                    label="Home"
                    to="/admin"
                    icon={<IconHome size={18} />}
                    exact
                />
                <SidebarNavLink
                    narrow={narrow}
                    label="Organizations"
                    to="/admin/organizations"
                    icon={<IconBuilding size={18} />}
                />
                <SidebarNavLink
                    narrow={narrow}
                    label="Admin Users"
                    to="/admin/users"
                    icon={<IconUser size={18} />}
                />
            </Stack>
        )
    }

    if (orgLoading) {
        return <Loader size="sm" style={{alignSelf: narrow ? 'center' : undefined}} />
    }

    if (!orgId) {
        return null
    }

    return (
        <Stack gap={narrow ? 6 : 'xs'}>
            {!narrow && (
                <Text fw={700} size="lg" mt="sm" mb="md" c="brand.9">
                    {org?.name}
                </Text>
            )}
            {!narrow && (
                <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={4}>
                    Overview
                </Text>
            )}
            <SidebarNavLink
                narrow={narrow}
                label="Dashboard"
                to={`/organizations/${orgId}`}
                icon={<IconLayoutDashboard size={18} />}
                exact
            />
            {!narrow && (
                <Text size="xs" fw={600} c="dimmed" tt="uppercase" mt="lg" mb={4}>
                    Analysis
                </Text>
            )}
            <SidebarNavLink
                narrow={narrow}
                label="Gap Analysis"
                to={`/organizations/${orgId}/gap-analysis`}
                icon={<IconTargetArrow size={18} />}
            />
            <SidebarNavLink
                narrow={narrow}
                label="Prospects by Capacity"
                to={`/organizations/${orgId}/prospects-by-capacity`}
                icon={<IconChartBar size={18} />}
            />
            <SidebarNavLink
                narrow={narrow}
                label="Capacity and Engagement"
                to={`/organizations/${orgId}/prospects-by-engagement`}
                icon={<IconHeartHandshake size={18} />}
            />
            <SidebarNavLink
                narrow={narrow}
                label="Conversion by Engagement"
                to={`/organizations/${orgId}/conversion-by-engagement-level`}
                icon={<IconTrendingUp size={18} />}
            />
            <SidebarNavLink
                narrow={narrow}
                label="Prospects by Giving Level"
                to={`/organizations/${orgId}/prospects-by-giving-level`}
                icon={<IconCoin size={18} />}
            />
            <SidebarNavLink
                narrow={narrow}
                label="Assigned vs Unassigned"
                to={`/organizations/${orgId}/assigned-vs-unassigned`}
                icon={<IconArrowsSplit size={18} />}
            />
            {!narrow && (
                <Text size="xs" fw={600} c="dimmed" tt="uppercase" mt="lg" mb={4}>
                    Data
                </Text>
            )}
            <SidebarNavLink
                narrow={narrow}
                label="Prospects"
                to={`/organizations/${orgId}/prospects`}
                icon={<IconUsers size={18} />}
            />
        </Stack>
    )
}

function AuthedLayout() {
    const navigate = useNavigate()
    const user = getStoredUser()!
    const theme = useMantineTheme()
    const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm})`)
    const [mobileDrawerOpened, {close: closeMobileDrawer, toggle: toggleMobileDrawer}] =
        useDisclosure(false)
    const [sidebarExpanded, {toggle: toggleSidebarExpanded}] = useDisclosure(true)
    const narrow = Boolean(isDesktop && !sidebarExpanded)
    const orgMatch = useMatch({
        from: '/_authed/organizations/$orgId',
        shouldThrow: false,
    })
    const location = useLocation()
    const adminMatch = location.pathname.startsWith('/admin')
    const hasNavbar = !!orgMatch || adminMatch
    const orgId = orgMatch?.params?.orgId

    const {data: org, isLoading: orgLoading} = useQuery({
        queryKey: ['organization', orgId],
        queryFn: () => getOrganization(orgId!),
        enabled: !!orgId,
    })

    useEffect(() => {
        closeMobileDrawer()
    }, [location.pathname, closeMobileDrawer])

    async function handleLogout() {
        try {
            await apiFetch('/auth/logout', {method: 'POST'})
        } catch (err) {
            console.error('Logout API call failed:', err)
        } finally {
            clearStoredUser()
            window.location.href = '/login'
        }
    }

    const useRfmForEngagement = org?.useRfmForEngagement ?? false

    return (
        <ScenarioProvider sessionOrgId={orgId}>
            <AppShell
                header={{height: 64}}
                navbar={
                    hasNavbar
                        ? {
                              width: narrow ? NAVBAR_WIDTH_COLLAPSED : NAVBAR_WIDTH_EXPANDED,
                              breakpoint: 'sm',
                              collapsed: {mobile: true},
                          }
                        : undefined
                }
                padding="lg"
                styles={{main: {backgroundColor: '#F7F6FA'}}}
            >
                <AppShell.Header
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0 16px',
                        background: '#1E1E46',
                        borderBottom: '2px solid #750075',
                        minWidth: 0,
                    }}
                >
                    <Group wrap="nowrap" gap="sm" style={{flex: 1, minWidth: 0}}>
                        <Burger
                            opened={mobileDrawerOpened}
                            onClick={toggleMobileDrawer}
                            hiddenFrom="sm"
                            size="sm"
                            color="white"
                            style={{flexShrink: 0}}
                            aria-label={mobileDrawerOpened ? 'Close menu' : 'Open menu'}
                        />
                        <Group
                            wrap="nowrap"
                            gap="xs"
                            style={{
                                cursor: 'pointer',
                                flex: 1,
                                minWidth: 0,
                            }}
                            onClick={() => navigate({to: '/'})}
                        >
                            <Box visibleFrom="sm" style={{display: 'flex', flexShrink: 0}}>
                                <img
                                    src={logoDarkUrl}
                                    alt=""
                                    height={28}
                                    style={{display: 'block', width: 'auto'}}
                                />
                            </Box>
                        </Group>
                    </Group>
                    <Group gap="sm" wrap="nowrap" visibleFrom="sm">
                        <Text c="rgba(255,255,255,0.8)" truncate maw={280}>
                            {user.name}
                        </Text>
                        <Button
                            variant="subtle"
                            leftSection={<IconLogout size={16} />}
                            onClick={handleLogout}
                            c="rgba(255,255,255,0.8)"
                        />
                    </Group>
                </AppShell.Header>
                {hasNavbar && (
                    <AppShell.Navbar
                        p={narrow ? 'xs' : 'md'}
                        style={{
                            backgroundColor: '#F5F4F9',
                            borderRight: '1px solid #E4E2ED',
                        }}
                        styles={{
                            navbar: {
                                minHeight: 0,
                            },
                        }}
                    >
                        <ScrollArea flex={1} mih={0} scrollbars="y" scrollbarSize={8} type="scroll">
                            <Group justify="flex-start" mb="sm" wrap="nowrap" visibleFrom="sm">
                                <SidebarNavLink
                                    variant="subtle"
                                    label={narrow ? 'Expand Menu' : 'Collapse Menu'}
                                    narrow={narrow}
                                    icon={
                                        narrow ? (
                                            <IconLayoutSidebarLeftExpand size={20} />
                                        ) : (
                                            <IconLayoutSidebarLeftCollapse size={20} />
                                        )
                                    }
                                    onClick={toggleSidebarExpanded}
                                />
                            </Group>
                            {user.is_admin && hasNavbar && !adminMatch && (
                                <Stack gap={narrow ? 6 : 'xs'}>
                                    <SidebarNavLink
                                        narrow={narrow}
                                        label="Admin Panel"
                                        to={orgId ? `/admin/organizations/${orgId}` : '/admin'}
                                        icon={<IconSettings size={18} />}
                                    />
                                    {orgId && (
                                        <ScenarioPlanningTrigger
                                            narrow={narrow}
                                            useRfmForEngagement={useRfmForEngagement}
                                        />
                                    )}
                                </Stack>
                            )}
                            <SidebarNavContent
                                narrow={narrow}
                                adminMatch={adminMatch}
                                orgLoading={orgLoading}
                                org={org}
                                orgId={orgId}
                            />
                        </ScrollArea>
                    </AppShell.Navbar>
                )}
                <AppShell.Main>
                    <ScenarioActiveBanner />
                    <Outlet />
                </AppShell.Main>
            </AppShell>
            <Drawer
                opened={mobileDrawerOpened}
                onClose={closeMobileDrawer}
                position="left"
                size={280}
                padding="md"
                withCloseButton={false}
                hiddenFrom="sm"
                styles={{
                    header: {display: 'none'},
                    body: {paddingTop: 'var(--mantine-spacing-md)'},
                }}
            >
                <Stack gap="md">
                    <Group align="flex-start" justify="space-between" wrap="nowrap" gap="sm">
                        <Stack gap={4} style={{flex: 1, minWidth: 0}}>
                            <Text fw={600} size="sm" truncate>
                                {user.name}
                            </Text>
                            <Text size="xs" c="dimmed">
                                {user.is_admin ? 'Administrator' : 'Client'}
                            </Text>
                        </Stack>
                        <CloseButton
                            size="lg"
                            aria-label="Close menu"
                            iconSize={18}
                            onClick={closeMobileDrawer}
                            style={{flexShrink: 0}}
                        />
                    </Group>
                    {user.is_admin && !adminMatch && (
                        <Stack gap="xs">
                            <NavLink
                                component={Link}
                                to={orgId ? `/admin/organizations/${orgId}` : '/admin'}
                                label="Admin Panel"
                                leftSection={<IconSettings size={18} />}
                                color="green"
                                variant="light"
                            />
                            {orgId && (
                                <ScenarioPlanningTrigger
                                    narrow={false}
                                    useRfmForEngagement={useRfmForEngagement}
                                />
                            )}
                        </Stack>
                    )}
                    {hasNavbar && (
                        <>
                            <Divider label="Navigation" labelPosition="left" />
                            <SidebarNavContent
                                narrow={false}
                                adminMatch={adminMatch}
                                orgLoading={orgLoading}
                                org={org}
                                orgId={orgId}
                            />
                        </>
                    )}
                    <Divider />
                    <Button
                        variant="light"
                        color="gray"
                        leftSection={<IconLogout size={18} />}
                        onClick={handleLogout}
                        fullWidth
                    >
                        Log out
                    </Button>
                </Stack>
            </Drawer>
        </ScenarioProvider>
    )
}
